import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSave, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import BreadCrumbs from "../../../../components/BreadCrumbs";
import ControlledRowTextField from "../../../../components/ControlledRowTextField";
import ControlledSelect from "../../../../components/ControlledSelect";
import ControlledCheckBoxField from "../../../../components/ControlledCheckBoxField";
import ButtonComponent from "../../../../components/ButtonComponent";
import { useSaveDimensionMutation } from "../../../../redux/services/CustomTypeService";

interface AddDimensionFormData {
  name: string;
  displayName: string;
  category: string;
  isRequired: boolean;
  selectionOrder: { value: number; label: string };
  imageUrl?: string;
}

const AddDimension: React.FC = () => {
  const { menuType, customMenuItemTypeId } = useParams<{ menuType: string; customMenuItemTypeId: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Get restaurant ID from Redux
  const selectedRestaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id
  );
  
  // Get the actual mutation hook
  const [saveDimensionMutation] = useSaveDimensionMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<AddDimensionFormData>({
    defaultValues: {
      name: "",
      displayName: "",
      category: "Srilankan",
      isRequired: true,
      selectionOrder: { value: 1, label: "1" },
      imageUrl: "",
    },
  });
  
  const selectionOrderOptions = Array.from({ length: 10 }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }));

  const onSubmit = async (data: AddDimensionFormData) => {
    setIsSubmitting(true);
    
    try {
      if (!selectedRestaurantId) {
        toast.error("Restaurant not selected. Please refresh and try again.");
        return;
      }

      if (!customMenuItemTypeId) {
        toast.error("Custom menu type ID not found. Please navigate back and try again.");
        return;
      }

      const dimensionData = {
        restaurantId: selectedRestaurantId,
        customMenuItemTypeId: customMenuItemTypeId,
        displayName: data.displayName,
        selectionOrder: data.selectionOrder.value,
        isRequired: data.isRequired,
      };
      
      await saveDimensionMutation(dimensionData).unwrap();
      
      toast.success("Dimension created successfully!");
      navigate(`/productCatalogue/product-groups/${menuType}/dimensions`, {
        state: { shouldRefetch: true }
      });
      
    } catch (error: any) {
      console.error("Error creating dimension:", error);
      const errorMessage = error?.data || error?.message || "Failed to create dimension. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayNameWatcher = watch("displayName");

  return (
    <>
      <BreadCrumbs 
        hasSubMenus={true} 
        customNameMap={{
          [customMenuItemTypeId || '']: menuType || 'Menu Type'
        }}
      />
      
      <Card className="mt-3">
        <CardHeader className="header-bg-color d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <h5 className="mb-0">Add Dimension for {menuType}</h5>
          </div>
        </CardHeader>
        
        <CardBody>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="d-flex align-items-center justify-content-between">              
              <Col md={5}>
                <ControlledRowTextField
                  label="Dimension Name"
                  inputName="displayName"
                  inputType="text"
                  placeholder="e.g., Choose Your Part, Select Style"
                  control={control}
                  rules={{
                    required: "Display name is required",
                    minLength: {
                      value: 3,
                      message: "Display name must be at least 3 characters",
                    },
                    maxLength: {
                      value: 50,
                      message: "Display name must be less than 50 characters",
                    },
                  }}
                />
                {errors.displayName && (
                  <span className="text-danger small">{errors.displayName.message}</span>
                )}
              </Col>    
              <Col md={5}>
                <ControlledSelect
                  label="Selection Order"
                  inputName="selectionOrder"
                  control={control}
                  optionsList={selectionOrderOptions}
                  rules={{ required: "Selection order is required" }}
                />
                {errors.selectionOrder && (
                  <span className="text-danger small">{errors.selectionOrder.message}</span>
                )}
              </Col>
              <Col md={2}>
                <ControlledCheckBoxField
                  label="Required Dimension"
                  inputName="isRequired"
                  control={control}
                />
                {errors.isRequired && (
                  <span className="text-danger small">{errors.isRequired.message}</span>
                )}
              </Col>
            </Row>

            <div className="mt-4">
              <div className="alert alert-info">
                <h6 className="mb-2">Dimension Information:</h6>
                <ul className="mb-0 small">
                  <li><strong>Display Name:</strong> Shown to customers during customization</li>
                  <li><strong>Selection Order:</strong> Determines the order of appearance in customization flow</li>
                  <li><strong>Menu Type:</strong> {menuType}</li>
                </ul>
              </div>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="secondary"
                onClick={() => navigate(`/productCatalogue/product-groups/${menuType}/dimensions`)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <ButtonComponent
                type="submit"
                isDisabled={isSubmitting || !displayNameWatcher}
                titel={isSubmitting ? "Creating..." : "Create Dimension"}
                icon={<FontAwesomeIcon icon={isSubmitting ? faSpinner : faSave} spin={isSubmitting} />}
              />
            </div>
          </Form>
        </CardBody>
      </Card>
    </>
  );
};

export default AddDimension;